import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment';

import Checkbox from '@material-ui/core/Checkbox';
import iconSet from '../../shared/images/teambuildr-selection.json';
import {
  fetchExercises,
  setEndDateData,
  setExercises,
  setSelectedStartDate,
  setSelectedEndDate,
  setStartDateData,
  setVTDashView,
} from './ducks/volumeTrackerDashboardActions';
import Button from '../../shared/_TBComponents/Button';
import DatePicker from '../../shared/_TBComponents/DatePicker';
import { dashboardTracker } from '../../shared/utils/amplitudeHelper';

const NewContainer = styled('div')`
  width: 100%;
  padding: 20px;
  display: flex;
  height: auto;
`;

const BackgroundContainer = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  background: white;
  border-radius: 8px;
  display: flex;
  justify-content: center;
`;

const UpperContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 200px;
  border-bottom: 2px solid #EEEEEE;
`;

const PlusIconContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 75px;
  height: 75px;
  border: 1px solid black;
`;

const Header = styled('div')`
  font-size: 23px;
  font-family: 'Nunito Sans';
  color: black;
  margin-top: 10px;
`;

const SubHeader = styled('div')`
  font-size: 16px;
  font-family: 'Nunito Sans';
  color: ${(props) => props.color};
`;

const SubHeaderContainer = styled('div')`
  font-size: 16px;
  font-family: 'Nunito Sans';
  color: black;
  margin-top: 20px;
  margin-bottom: 16px;
  display: flex;
  width: 100%;
`;

const CenterContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 85%;
  height: 100%;
`;

const DatePickerContainer = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 16px;
  gap: 20px;
`;

const LowerBox = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 500px;
  margin-top: 32px;
`;

const RightBox = styled('div')`
  display: flex;
  width: 48%;
  height: 100%;
  flex-direction: column;
`;

const LeftBox = styled('div')`
  display: flex;
  width: 48%;
  height: 100%;
  flex-direction: column;
`;

const BoxTitle = styled('div')`
  display: flex;
  height: 25px;
  width: 100%;
  font-size: 16px;
  font-family: 'Nunito Sans';
`;

const SearchAndIcon = styled('div')`
  display: flex;
  align-items: center;
  border: 1px solid #EEEEEE;
  border-radius: 50px;
  background: white;
  transition: max-width 0.5s ease;
  overflow: hidden;
  width: 100%;
`;

const SearchIconContainer = styled('div')`
  padding-left: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 5px;
`;

const SearchContainer = styled('input')`
  display: flex;
  height: 40px;
  border: none;
  background: none;
  padding-left: 5px;
  width: 100%;
`;

const SearchAndFilter = styled('div')`
  display: flex;
  width: 100%;
  margin-top: 10px;
`;

const ExercisesBox = styled('div')`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  scrollbar-width: thin;
  scrollbar-color: lightgrey transparent;
  background: #FCFCFC;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  height: 100%;
`;

const AvailableExercise = styled('div')`
  display: flex;
  min-height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${(props) => props.color};
  padding-left: 15px;
`;

const RemoveContainer = styled('div')`
  cursor: pointer;
`;

const NewVTDashboard = ({
  setIsOverview,
  setNewDash,
  selectedExercises,
  setSelectedExercises,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const currentUser = useSelector(
    (state) => state.auth.data.currentUser,
  );

  const availableExercises = useSelector(
    (state) => state.volumeTrackerDashboard.data.availableExercises,
  );
  const selectedStartDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedStartDate,
  );
  const selectedEndDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedEndDate,
  );

  const [searchedList, setSearchedList] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [areChecksDisabled, setAreChecksDisabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleStartDate = (date) => {
    const start = moment(date);
    let end = moment(selectedEndDate);
    if (!end.isValid() || end.isBefore(start) || end.diff(start, 'days') >= 56) {
      end = moment.min(start.clone().add(55, 'days'), moment());
      dispatch(setSelectedEndDate(end));
      dispatch(setEndDateData(end.format('YYYY-MM-DD')));
    }
    dispatch(setSelectedStartDate(start));
    dispatch(setStartDateData(start.format('YYYY-MM-DD')));
  };

  const handleEndDate = (date) => {
    let start = moment(selectedStartDate);
    const end = moment(date);
    if (end.isBefore(start) || end.diff(start, 'days') >= 56) {
      start = moment.min(end.clone().subtract(55, 'days'), moment());
      dispatch(setSelectedStartDate(start));
      dispatch(setStartDateData(start.format('YYYY-MM-DD')));
    }
    dispatch(setSelectedEndDate(end));
    dispatch(setEndDateData(end.format('YYYY-MM-DD')));
  };

  useEffect(() => {
    const searchedArray = availableExercises.filter(
      (exercise) => exercise.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    setSearchedList(searchedArray);
  }, [searchTerm, availableExercises]);

  useEffect(() => {
    const exerciseCount = Object.values(selectedExercises).filter(
      (value) => Object.keys(value).length > 0,
    ).length;

    setAreChecksDisabled(exerciseCount >= 10);
  }, [selectedExercises]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchExercises(currentUser.accountCode));
    }
  }, [currentUser]);

  const exerciseResolver = (exercise) => {
    if (Object.keys(exercise).length) {
      return (
        <AvailableExercise color={theme.colors.text}>
          <div>{exercise.name}</div>
          <RemoveContainer onClick={() => {
            const newOb = { ...selectedExercises };
            delete newOb[exercise.id];
            setSelectedExercises(newOb);
          }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='remove'
              color='#9E9E9E'
            />
          </RemoveContainer>
        </AvailableExercise>
      );
    }
    return null;
  };

  const isLoadDisabled = (selectedExercisesObject) => !Object.values(
    selectedExercisesObject,
  ).some(
    (exercise) => Object.keys(exercise).length > 0,
  );

  const submitExercises = (selectedExercisesObject) => {
    const submissionArray = Object.values(selectedExercisesObject)
      .filter((exercise) => Object.keys(exercise).length)
      .map(({
        id, name, type, unitAbbr, unitName, inputType,
      }) => ({
        id,
        name,
        type,
        unitAbbr,
        unitName,
        inputType,
      }));

    dispatch(setExercises(submissionArray));
    dispatch(setNewDash(false));
    setIsOverview();
    setVTDashView('overview');
  };

  return (
    <NewContainer>
      <BackgroundContainer>
        <CenterContainer>
          <UpperContainer>
            <PlusIconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={25}
                icon='plus'
                color='black'
              />
            </PlusIconContainer>
            <Header>
              New Dashboard
            </Header>
          </UpperContainer>
          <SubHeaderContainer>
            <SubHeader color={theme.colors.darkGray}>
              Select the date range (max 8 weeks) and up to 10 exercises to add to this dashboard.
            </SubHeader>
          </SubHeaderContainer>
          <BoxTitle>
            Date Range
          </BoxTitle>
          <DatePickerContainer>
            <DatePicker
              singleDate={false}
              startDate={selectedStartDate ? moment(selectedStartDate).toDate() : null}
              endDate={selectedEndDate ? moment(selectedEndDate).toDate() : null}
              setStartDate={handleStartDate}
              setEndDate={handleEndDate}
              maxDate={moment().toDate()}
              minDate={moment('1970-01-01').toDate()}
            />
          </DatePickerContainer>
          <LowerBox>
            <LeftBox>
              <BoxTitle>
                All Exercises
              </BoxTitle>
              <SearchAndFilter>
                <SearchAndIcon id='search-container' onClick={() => setIsSearchFocused(true)} isFocused={isSearchFocused}>
                  <SearchIconContainer id='search-icon-container'>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={20}
                      icon='magnifying-glass-alt'
                      color='#9E9E9E'
                    />
                  </SearchIconContainer>
                  <SearchContainer
                    placeholder='Search'
                    id='search-container-input'
                    value={searchTerm}
                    onFocus={() => setIsSearchFocused(true)}
                    onChange={(inputValue) => {
                      setSearchTerm(inputValue.target.value);
                    }}
                  />
                </SearchAndIcon>
              </SearchAndFilter>
              <ExercisesBox>
                {!searchTerm.length ? availableExercises.map(
                  (exercise) => (
                    <AvailableExercise color={theme.colors.text}>
                      <div>{exercise.name}</div>
                      <div>
                        <Checkbox
                          disabled={
                            areChecksDisabled
                            && (!selectedExercises[exercise.id]
                              || Object.keys(selectedExercises[exercise.id]).length === 0)
                          }
                          key={exercise.id}
                          checked={
                            !!(selectedExercises[exercise.id]
                            && Object.keys(selectedExercises[exercise.id]).length)
                          }
                          onChange={() => {
                            const newOb = { ...selectedExercises };
                            if (!newOb[exercise.id]) {
                              newOb[exercise.id] = exercise;
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length
                            ) {
                              newOb[exercise.id] = {};
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length === 0) {
                              newOb[exercise.id] = exercise;
                            }
                            (setSelectedExercises(newOb));
                          }}
                          color='primary'
                        />
                      </div>
                    </AvailableExercise>
                  ),
                ) : searchedList.map(
                  (exercise) => (
                    <AvailableExercise color={theme.colors.text}>
                      <div>{exercise.name}</div>
                      <div>
                        <Checkbox
                          disabled={
                            areChecksDisabled
                            && (!selectedExercises[exercise.id]
                              || Object.keys(selectedExercises[exercise.id]).length === 0)
                          }
                          checked={
                            !!(selectedExercises[exercise.id]
                            && Object.keys(selectedExercises[exercise.id]).length)
                          }
                          onChange={() => {
                            const newOb = { ...selectedExercises };
                            if (!newOb[exercise.id]) {
                              newOb[exercise.id] = exercise;
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length
                            ) {
                              newOb[exercise.id] = {};
                            } else if (
                              newOb[exercise.id] && Object.keys(newOb[exercise.id]).length === 0) {
                              newOb[exercise.id] = exercise;
                            }
                            (setSelectedExercises(newOb));
                          }}
                          color='primary'
                        />
                      </div>
                    </AvailableExercise>
                  ),
                )}
              </ExercisesBox>
            </LeftBox>
            <RightBox>
              <BoxTitle>
                Selected Exercises
              </BoxTitle>
              <ExercisesBox>
                {Object.values(selectedExercises).filter((e) => Object.keys(e).length)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((exercise) => exerciseResolver(exercise))}
              </ExercisesBox>
            </RightBox>
          </LowerBox>
          <Button
            bottom
            fullWidth
            customBorderRadius='4px'
            name='Load Dashboard'
            txtColor='#FFFFFF'
            upperCase
            large
            disabled={isLoadDisabled(selectedExercises)}
            noBorder
            bgColor='#444444'
            styledTextProps={{ fontSize: '14px' }}
            styledWrapperProps={{ margin: '20px 0' }}
            onClick={() => {
              submitExercises(selectedExercises);
              // Amplitude tracking
              dashboardTracker('Volume Tracker', 'New Dashboard', 'Load dashboard clicked');
            }}
          />
        </CenterContainer>
      </BackgroundContainer>
    </NewContainer>
  );
};

NewVTDashboard.propTypes = {
  setIsOverview: PropTypes.func.isRequired,
  setNewDash: PropTypes.func.isRequired,
  selectedExercises: PropTypes.instanceOf(Object).isRequired,
  setSelectedExercises: PropTypes.func.isRequired,
};

export default NewVTDashboard;
